import { Box, Flex, Message, Modal, Text, useAppTheme } from '@workwhile/ui'
import React from 'react'
import { useDayforcePanelContextValue } from '../context/DayforcePanelProvider'
import { formatInTimeZone } from 'date-fns-tz'
import { DAYFORCE_SHIFTS_DEFAULT_TIMEZONE } from 'pages/Dayforce/constants'

export const DayforceJobDetailsModal = ({
  onClose,
}: {
  onClose: () => void
}) => {
  const { space } = useAppTheme()

  const {
    state: { booking, job },
  } = useDayforcePanelContextValue()

  if (!job) {
    return (
      <Message variant="error" borderRadius="small">
        Error loading dayforce job details
      </Message>
    )
  }

  if (!booking) {
    return (
      <Message variant="error" borderRadius="small">
        Error loading dayforce booking details
      </Message>
    )
  }

  const startsAt = formatInTimeZone(
    booking.startsAt,
    DAYFORCE_SHIFTS_DEFAULT_TIMEZONE,
    'EEEE, MMMM d, h:mm a z'
  )
  const finishesAt = formatInTimeZone(
    booking.finishesAt,
    DAYFORCE_SHIFTS_DEFAULT_TIMEZONE,
    'EEEE, MMMM d, h:mm a z'
  )

  const noWorkersRequired = booking.workersRequired === 0

  return (
    <Modal open onClose={onClose} title="Dayforce Booking Details">
      {noWorkersRequired ? (
        <Message variant="warning" borderRadius="small">
          No workers required for this booking. This is likely a canceled job.
        </Message>
      ) : null}
      <Flex flexDirection="column" gap={space[1]}>
        <Text>
          <Text as="span" fontWeight="bold">
            Booking ID:{' '}
          </Text>
          {booking.bookingId}
        </Text>
        {booking.hazmatOldBookingId ? (
          <Text>
            <Text as="span" fontWeight="bold">
              "Old" Booking ID:{' '}
            </Text>
            {booking.hazmatOldBookingId}
          </Text>
        ) : null}
        <Text>
          <Text as="span" fontWeight="bold">
            Job Name:{' '}
          </Text>
          {booking.jobName || ''}
        </Text>
        <Text>
          <Text as="span" fontWeight="bold">
            Position ID:{' '}
          </Text>
          {booking.position.id}
        </Text>
        <Text>
          <Text as="span" fontWeight="bold">
            Position Name:{' '}
          </Text>
          {booking.position.name} ({booking.level.name})
        </Text>
        <Text>
          <Text as="span" fontWeight="bold">
            Starts At:{' '}
          </Text>
          {startsAt}
        </Text>
        <Text>
          <Text as="span" fontWeight="bold">
            Finishes At:{' '}
          </Text>
          {finishesAt}
        </Text>
        <Text>
          <Text as="span" fontWeight="bold">
            Workers Assigned:{' '}
          </Text>
          {booking.workersAssigned}
        </Text>
        <Text>
          <Text as="span" fontWeight="bold">
            Workers Required:{' '}
          </Text>
          {booking.workersRequired}
        </Text>
        <Text>
          <Text as="span" fontWeight="bold">
            Backup Workers Assigned:{' '}
          </Text>
          {booking.backupWorkersAssigned}
        </Text>
        <Text>
          <Text as="span" fontWeight="bold">
            Backup Workers Required:{' '}
          </Text>
          {booking.backupWorkersRequired}
        </Text>
        <Text>
          <Text as="span" fontWeight="bold">
            Bill Rate:{' '}
          </Text>
          {booking.billRate}
        </Text>
        <Text>
          <Text as="span" fontWeight="bold">
            Pay Rate:{' '}
          </Text>
          {booking.payRate}
        </Text>
        <Text>
          <Text as="span" fontWeight="bold">
            Bonus:{' '}
          </Text>
          {booking.bonus}
        </Text>
        <Text>
          <Text as="span" fontWeight="bold">
            Travel Stipend:{' '}
          </Text>
          {booking.travelStipend}
        </Text>
        <Flex flexDirection="column" gap={space[1]}>
          <Text fontWeight="bold">Dress Code:</Text>
          <Box
            as="pre"
            margin={0}
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: 'inherit',
            }}
          >
            {booking.dressCode}
          </Box>
        </Flex>
        <Flex flexDirection="column" gap={space[1]}>
          <Text fontWeight="bold">Special Instructions:</Text>
          <Box
            as="pre"
            margin={0}
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: 'inherit',
            }}
          >
            {booking.specialInstructions}
          </Box>
        </Flex>
        <Flex flexDirection="column" gap={space[1]}>
          <Text fontWeight="bold">Additional Tasks:</Text>
          <Box
            as="pre"
            margin={0}
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: 'inherit',
            }}
          >
            {booking.additionalTasks}
          </Box>
        </Flex>
        <Text fontWeight="bold">Managers:</Text>
        <Flex flexDirection="column" gap={space[1]}>
          {job.managers.map((manager) => (
            <Flex
              key={manager.id}
              flexDirection="column"
              gap={space[1]}
              marginLeft={space[2]}
            >
              <Text fontWeight="bold">
                - {manager.firstName} {manager.lastName}
              </Text>
              <Text paddingLeft="1rem">
                <Text as="span" fontWeight="bold">
                  Email:{' '}
                </Text>
                {manager.email}
              </Text>
              <Text paddingLeft="1rem">
                <Text as="span" fontWeight="bold">
                  Phone:{' '}
                </Text>
                {manager.phoneNumber}
              </Text>
              <Text paddingLeft="1rem">
                <Text as="span" fontWeight="bold">
                  Primary Contact:{' '}
                </Text>
                {manager.primaryContact ? 'Yes' : 'No'}
              </Text>
              <Text paddingLeft="1rem">
                <Text as="span" fontWeight="bold">
                  Booker:{' '}
                </Text>
                {manager.booker ? 'Yes' : 'No'}
              </Text>
            </Flex>
          ))}
        </Flex>
      </Flex>
    </Modal>
  )
}
